<script lang="ts">
export default {
  name: 'SICCodesAutocompleteInput',
};
</script>

<script setup lang="ts">
import { referenceService } from '@/services/api';
import { SelectOption } from '@/types/components/Select';
import { ClientIndustry } from '@/types/Industry';
import { onBeforeMount, ref } from 'vue';
import Combobox from './v2/Combobox.vue';

const props = defineProps<{
  large?: boolean;
}>();

// ------
// model
// ------
const code = defineModel<string>({ default: '' });

// -----
// refs
// -----
const sicCodeSelectOptions = ref<SelectOption[]>([]);

// -----------
// lifecycles
// -----------
onBeforeMount(async () => {
  const sicCodes = await referenceService.sicCodes();
  sicCodeSelectOptions.value = toSICCodeSelectOptions(sicCodes);
});

// -----------------------
// functions and handlers
//------------------------
function toSICCodeSelectOptions(sicCodes: ClientIndustry[]): SelectOption[] {
  interface SICCodeGroup {
    categoryCode: string;
    categoryName: string;
    sicCodes: ClientIndustry[];
  }

  const categoryMap: Map<string, SICCodeGroup> = sicCodes.reduce((acc, sicCode) => {
    if (!acc.has(sicCode.categoryCode)) {
      acc.set(sicCode.categoryCode, {
        categoryCode: sicCode.categoryCode,
        categoryName: sicCode.categoryName,
        sicCodes: [],
      } as SICCodeGroup);
    }

    acc.get(sicCode.categoryCode)?.sicCodes.push(sicCode);
    return acc;
  }, new Map<string, SICCodeGroup>());

  if (categoryMap.size === 0) {
    return [];
  }

  const options: SelectOption[] = [];

  for (const group of Array.from(categoryMap.values()).sort(
    (a, b) => Number(a.categoryCode) - Number(b.categoryCode),
  )) {
    // create header option
    options.push({
      value: group.categoryCode.toString(),
      label: `${group.categoryCode} ${group.categoryName}`,
      htmlLabel: `<span class="flex gap-4"><b>${group.categoryCode}</b> ${group.categoryName}</span>`,
      readOnly: true,
      isLabelHTML: true,
    });

    for (const sicCode of group.sicCodes) {
      options.push({
        value: sicCode.sicCode,
        label: `${sicCode.sicCode} ${sicCode.name}`,
        htmlLabel: `<span class="pl-4 flex gap-4 text-xs"><b>${sicCode.sicCode}</b> ${sicCode.name}</span>`,
        isLabelHTML: true,
      });
    }
  }

  return options;
}
</script>

<template>
  <Combobox
    class="min-w-0 grow"
    :input-class="props.large ? '' : 'input-sm'"
    v-model="code"
    :options="sicCodeSelectOptions.filter((o) => !o.readOnly)"
    :placeholder="'SIC Code'" />
</template>
